import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, Clock, UserPlus, Receipt, RefreshCw, Map } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { adminAPI } from '../../services/api';
import { formatPrice } from '../../utils/helpers';
import { KpiCard, GrowthBadge } from '../../components/admin/dashboard/KpiCard';
import { RevenueChart } from '../../components/admin/dashboard/RevenueChart';
import { RecentOrdersTable } from '../../components/admin/dashboard/RecentOrdersTable';

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [error, setError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      setData(await adminAPI.getExecutiveDashboard(days));
      setError(false);
    } catch {
      setError(true);
    } finally { setRefreshing(false); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const k = data?.kpis;
  const monthName = new Date().toLocaleDateString('es-MX', { month: 'long', timeZone: 'UTC' });

  return (
    <AdminLayout>
      <style>{`@keyframes kpiIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }`}</style>
      <div className="space-y-8 max-w-7xl" data-testid="admin-dashboard">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-orange-400 font-semibold">Centro de mando</p>
            <h1 className="text-3xl font-bold text-white tracking-tight" data-testid="admin-dashboard-title">War Room</h1>
            <p className="text-gray-500 text-sm mt-1">Resumen ejecutivo de <span className="capitalize">{monthName}</span> (hora UTC)</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/warroom" data-testid="dashboard-live-map-link"
              className="inline-flex items-center gap-2 text-xs font-semibold text-gray-300 bg-white/5 hover:bg-white/10 ring-1 ring-white/10 rounded-full px-4 py-2 transition-colors">
              <Map className="w-4 h-4" /> Mapa en vivo
            </Link>
            <button onClick={load} disabled={refreshing} data-testid="dashboard-refresh-btn"
              className="inline-flex items-center gap-2 text-xs font-semibold text-gray-300 bg-white/5 hover:bg-white/10 ring-1 ring-white/10 rounded-full px-4 py-2 transition-colors">
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> Actualizar
            </button>
          </div>
        </div>

        {error && !data && (
          <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-300 p-4 text-sm" data-testid="dashboard-error">
            No se pudieron cargar las métricas. Intenta actualizar.
          </div>
        )}

        {!data ? (!error && (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
            {[0, 1, 2, 3].map(i => <div key={i} className="h-36 rounded-2xl bg-gray-800/60 animate-pulse" />)}
          </div>
        )) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
              <KpiCard testid="kpi-sales-month" label="Ventas del mes" icon={DollarSign} accent="bg-orange-500" delay={0}
                value={formatPrice(k.sales_month.value)} footer={<GrowthBadge pct={k.sales_month.change_pct} testid="kpi-sales-month-growth" />} />
              <KpiCard testid="kpi-pending-orders" label="Pedidos pendientes" icon={Clock} accent="bg-amber-400" delay={80}
                value={k.pending_orders.value.toLocaleString('es-MX')}
                footer={<span className="text-xs text-gray-500">{k.pending_orders.new_this_week} nuevos esta semana · sin entregar ni cancelar</span>} />
              <KpiCard testid="kpi-new-users" label="Nuevos usuarios" icon={UserPlus} accent="bg-sky-500" delay={160}
                value={k.new_users.value.toLocaleString('es-MX')} footer={<GrowthBadge pct={k.new_users.change_pct} testid="kpi-new-users-growth" />} />
              <KpiCard testid="kpi-avg-ticket" label="Ticket promedio" icon={Receipt} accent="bg-emerald-500" delay={240}
                value={formatPrice(k.avg_ticket)}
                footer={<span className="text-xs text-gray-500">{k.orders_month.value} pedidos · {k.active_buyers_30d} compradores activos (30 d)</span>} />
            </div>

            <RevenueChart series={data.series} total={data.series_total} days={days} onDaysChange={setDays} />

            <RecentOrdersTable orders={data.recent_orders} />
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
